import React from 'react'
import { Button, Drawer, Flex, Form, notification } from 'antd'
import { AntdForm } from '@/common/components/AntdForm'
import { handleApiError } from '@/utils/api.tsx'
import {
  useCreateWorkableEntityConfigMutation,
  useUpdateWorkableEntityConfigMutation,
} from '@/features/portal-configuration/portal-configuration.api.ts'
import {
  CreateWorkableEntityConfigRequest,
  WorkableEntityConfigResponseItem,
} from '@/features/portal-configuration/workable-configuration-request.def.ts'
import { CountryOptions } from '@/common/types/work-info.def.ts'
import { AntdFormRules } from '@/common/components/AntdForm/rules.tsx'

type EditWorkableConfigDrawerProps = {
  open: boolean
  onClose: () => void
  config?: WorkableEntityConfigResponseItem
  onSuccess?: () => void
}

type EditWorkableConfigurationFormValues = {
  name: string
  country: string
  subdomain: string
  apiToken?: string | null
}


export const EditWorkableConfigDrawer: React.FC<EditWorkableConfigDrawerProps> = ({
  open,
  onClose,
  config,
  onSuccess,
}) => {
  const isEditMode = !!config
  const [form] = Form.useForm<EditWorkableConfigurationFormValues>()
  const [createConfig, { isLoading: isCreating }] = useCreateWorkableEntityConfigMutation()
  const [updateConfig, { isLoading: isEditing }] = useUpdateWorkableEntityConfigMutation()
  const isLoading = isCreating || isEditing

  const onFinish = async (data: EditWorkableConfigurationFormValues) => {

    const result = isEditMode ? await updateConfig({
      ...data,
      id: config.id,
    }) : await createConfig(data as CreateWorkableEntityConfigRequest)

    if ('data' in result) {
      notification.success({
        message: 'Success',
        description: 'Updated successfully',
      })

      form.resetFields()
      onClose()
      onSuccess?.()
    } else if ('error' in result) {
      handleApiError(result.error, 'Failed to update config')
    }
  }

  const getInitialValues = () => {
    if (!config) return undefined

    return {
      ...config,
      apiToken: undefined,
    } as EditWorkableConfigurationFormValues
  }

  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  return (
    <Drawer
      title={`${isEditMode ? 'Edit' : 'Create'} Workable API Config`}
      placement="right"
      onClose={handleCancel}
      open={open}
      width={600}
    >
      <Form
        form={form} layout="vertical" onFinish={onFinish} initialValues={getInitialValues()}
      >
        <AntdForm.SectionWrapper>
          <AntdForm.Section>
            <AntdForm.Select
              fieldProps={{ name: 'country', rules: [AntdFormRules.required] }}
              options={CountryOptions}
            />
            <AntdForm.Input
              fieldProps={{
                name: 'name',
                label: 'Member Name',
                rules: [AntdFormRules.required],
                span: 16,
              }}
            />
            <AntdForm.Input
              fieldProps={{
                name: 'subdomain',
                label: 'Sub-Domain',
                span: 24,
                rules: [AntdFormRules.required],
              }}
            />
            <AntdForm.Password
              fieldProps={{
                name: 'apiToken',
                label: 'API Token',
                span: 24,
                rules: [AntdFormRules.requiredIf(!config)],
              }}
              type="password"
              placeholder="Input new API token to update. Current token will not be displayed."
            />
          </AntdForm.Section>
        </AntdForm.SectionWrapper>

        <Flex justify="flex-end" gap="small" style={{ marginTop: 24 }}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            Save Changes
          </Button>
        </Flex>
      </Form>
    </Drawer>
  )
}
